import React, { useState } from "react";
import { WeekCell } from "./WeekCell";
import DailyPlanTable from "./DailyPlanTable";
import { RichWeeklyCell } from "./richPlanText";
import { C, TABLE } from "../constants";
import TableColumnsMenu from "./TableColumnsMenu";
import { useTablePersonalize } from "../hooks/useTablePersonalize";

function EditableWeeklyCell({ value, editing, onStart, onChange, onSave, onCancel }) {
  if (!editing) {
    return (
      <div
        onClick={onStart}
        title="Click to edit"
        style={{ cursor: "pointer", minHeight: 28 }}
      >
        <RichWeeklyCell value={value} />
        <span style={{ fontSize: 10, color: C.muted }}>✎ edit</span>
      </div>
    );
  }
  return (
    <div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={8}
        style={{
          width: "100%", padding: "8px 10px", fontSize: 12.5, lineHeight: 1.5,
          border: `1.5px solid ${C.primary}`, borderRadius: 8, boxSizing: "border-box",
          fontFamily: "inherit", resize: "vertical",
        }}
      />
      <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
        <button type="button" onClick={onSave} style={{
          background: C.primary, color: "#fff", border: "none", borderRadius: 6,
          padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer",
        }}>
          Save
        </button>
        <button type="button" onClick={onCancel} style={{
          background: "transparent", color: C.muted, border: `1px solid ${C.border}`,
          borderRadius: 6, padding: "5px 10px", fontSize: 12, cursor: "pointer",
        }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export const PlanTable = ({
  plan,
  calMap,
  calendarMeta,
  onDayRegenerated,
  moduleKey,
  editMode = false,
  onPlanChange,
  focusWeek = null,
  focusDay = null,
  focusToken = null,
  memberMeta = null,
  scheduleLayout = undefined,
  onScheduleLayoutChange,
}) => {
  const [editCell, setEditCell] = useState(null); // { ri, ci, value }
  const weeklyCols = useTablePersonalize("weekly");

  if (!plan?.rows?.length) return null;
  const isDaily = plan.module === "daily" || moduleKey === "daily";
  if (isDaily) {
    return (
      <DailyPlanTable
        plan={{ ...plan, module: "daily" }}
        calMap={calMap}
        calendarMeta={calendarMeta}
        onDayRegenerated={onDayRegenerated}
        editMode={editMode}
        onPlanChange={onPlanChange}
        focusWeek={focusWeek}
        focusDay={focusDay}
        focusToken={focusToken}
        memberMeta={memberMeta}
        scheduleLayout={scheduleLayout}
        onScheduleLayoutChange={onScheduleLayoutChange}
      />
    );
  }

  const columns = Array.isArray(plan.columns) && plan.columns.length > 0
    ? plan.columns
    : (plan.rows[0] || []).map((_, i) => ["Week", "Days", "Subjects Covered", "Project Milestone", "Exam Focus"][i] || `Col ${i + 1}`);

  const columnDefs = columns.map((col, i) => ({
    id: `c${i}`,
    label: col,
    required: i < 2,
    index: i,
    icon: /week/i.test(col) ? "ti-calendar-week"
      : /day/i.test(col) ? "ti-calendar"
      : /subject/i.test(col) ? "ti-books"
      : /milestone/i.test(col) ? "ti-flag"
      : /exam/i.test(col) ? "ti-file-certificate"
      : /homework/i.test(col) ? "ti-moon"
      : "ti-layout-navbar",
  }));
  const visibleCols = columnDefs.filter((c) => weeklyCols.isOn(c.id));

  const saveWeeklyCell = () => {
    if (!editCell || !onPlanChange) return;
    const nextRows = plan.rows.map((row, ri) => {
      if (ri !== editCell.ri) return row;
      const copy = [...row];
      copy[editCell.ci] = editCell.value;
      return copy;
    });
    onPlanChange({ ...plan, rows: nextRows });
    setEditCell(null);
  };

  return (
    <div style={{ overflowX: "auto", borderRadius: 8, border: `1px solid ${C.border}` }}>
      <div style={{
        display: "flex", justifyContent: "flex-end", padding: "8px 12px",
        borderBottom: `1px solid ${C.border}`, background: C.surface,
      }}>
        <TableColumnsMenu
          columns={columnDefs}
          isOn={weeklyCols.isOn}
          toggle={weeklyCols.toggle}
          header={weeklyCols.header}
          rename={weeklyCols.rename}
          reset={weeklyCols.reset}
        />
      </div>
      {editMode && (
        <div style={{
          padding: "8px 12px", fontSize: 12, color: C.primary, fontWeight: 600,
          background: TABLE.editBanner, borderBottom: `1px solid ${C.border}`,
        }}>
          Edit mode on — click Subjects / Milestone / Exam cells to correct or add lines.
        </div>
      )}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 600 }}>
        <thead>
          <tr style={{ background: TABLE.headBg }}>
            {visibleCols.map((col, i) => (
              <th key={col.id} style={{
                padding: "10px 14px", textAlign: "left", color: TABLE.headText,
                fontWeight: 700, fontSize: 11, borderRight: `1px solid ${TABLE.headBorder}`,
                textTransform: "uppercase", letterSpacing: "0.04em"
              }}>
                {weeklyCols.header(col.id, col.label)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {plan.rows.map((row, ri) => {
            const col1 = String(row[0] ?? "");
            const rowBg = col1.includes("AU") ? C.auBg
              : col1.includes("Mock") ? C.mockBg
                : col1.includes("IA") ? C.warningBg
                  : ri % 2 === 0 ? C.surface : TABLE.rowAlt;
            return (
              <tr key={ri} style={{ borderBottom: `1px solid ${C.border}`, background: rowBg }}>
                {visibleCols.map((col) => {
                  const ci = col.index;
                  return (
                  <td key={col.id} style={{
                    padding: "9px 14px", color: C.text,
                    verticalAlign: "top", lineHeight: 1.55,
                    borderRight: `1px solid ${C.border}`,
                    maxWidth: ci >= 2 ? 320 : undefined,
                  }}>
                    {ci === 0 ? (
                      <WeekCell value={row[ci]} />
                    ) : editMode && ci >= 2 ? (
                      <EditableWeeklyCell
                        value={
                          editCell?.ri === ri && editCell?.ci === ci
                            ? editCell.value
                            : String(row[ci] ?? "").replace(/\\n/g, "\n")
                        }
                        editing={editCell?.ri === ri && editCell?.ci === ci}
                        onStart={() =>
                          setEditCell({
                            ri,
                            ci,
                            value: String(row[ci] ?? "").replace(/\\n/g, "\n"),
                          })
                        }
                        onChange={(v) => setEditCell((c) => ({ ...c, value: v }))}
                        onSave={saveWeeklyCell}
                        onCancel={() => setEditCell(null)}
                      />
                    ) : (
                      <RichWeeklyCell value={row[ci]} />
                    )}
                  </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
